import React, { useEffect, useRef, useState } from 'react';
import { FaCloudUploadAlt } from 'react-icons/fa';

export default function UploadDropzone({
    value = null,
    onChange = null,
    title = 'Arraste e solte o arquivo ou clique para selecionar',
    icon = null,
    style = null,
    accept = null,
    disabled = false,
    multiple = false,
    // Arrastar um arquivo para dentro da janela rola a tela até esta área ficar no centro. Liga por tela.
    centerOnDrag = false,
}) {
    const fileRef = useRef(null);
    const areaRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const nomes = Array.isArray(value) ? value.map(f => f?.name).filter(Boolean) : (value?.name ? [value.name] : []);

    // `centerOnDrag`, uma vez por arraste: os dragenter e dragleave da página vêm aos pares, e com a conta em zero o
    // arquivo saiu da janela. Na captura, porque esta área para a propagação dos dela.
    useEffect(() => {
        if (!centerOnDrag || disabled) return undefined;
        let dentro = 0;
        const comArquivo = (e) => e.dataTransfer?.types?.includes('Files');
        const entrou = (e) => {
            if (!comArquivo(e)) return;
            if (dentro === 0) areaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            dentro += 1;
        };
        const saiu = (e) => { if (comArquivo(e)) dentro = Math.max(0, dentro - 1); };
        const soltou = () => { dentro = 0; };
        window.addEventListener('dragenter', entrou, true);
        window.addEventListener('dragleave', saiu, true);
        window.addEventListener('drop', soltou, true);
        return () => {
            window.removeEventListener('dragenter', entrou, true);
            window.removeEventListener('dragleave', saiu, true);
            window.removeEventListener('drop', soltou, true);
        };
    }, [centerOnDrag, disabled]);

    const resetDragState = () => {
        setIsDragging(false);
    };

    // Com `multiple`, o onChange recebe a lista de arquivos; sem ele, só o primeiro, como sempre.
    const processFiles = (lista) => {
        const arquivos = Array.from(lista ?? []);
        if (!arquivos.length || disabled) return;
        if (onChange) onChange(multiple ? arquivos : arquivos[0]);
    };

    const handleFileUpload = (event) => {
        processFiles(event.target.files);
        event.target.value = '';
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!disabled) setIsDragging(true);
    };

    const handleDragEnter = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (disabled) return;
        if (event.dataTransfer?.types?.includes('Files')) {
            setIsDragging(true);
        }
    };

    const handleDragLeave = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (disabled) return;

        if (!event.currentTarget.contains(event.relatedTarget)) {
            setIsDragging(false);
        }
    };

    const handleDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (disabled) {
            resetDragState();
            return;
        }
        resetDragState();
        processFiles(event.dataTransfer?.files);
    };

    return (
        <>
            <input
                ref={fileRef}
                onChange={handleFileUpload}
                type="file"
                style={{ display: 'none' }}
                multiple={multiple}
                accept={accept ?? undefined}
                disabled={disabled}
            />
            <div
                ref={areaRef}
                onClick={() => {
                    if (!disabled) fileRef.current?.click();
                }}
                onDragEnter={handleDragEnter}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                title={title}
                className={`d-flex flex-column justify-content-center align-items-center text-center p-4 ${disabled ? 'border-secondary opacity-50' : isDragging ? 'bg-opacity-10 border-primary shadow-sm' : 'bg-white border-secondary'}`}
                style={{
                    backgroundColor: !disabled && isDragging ? '#f7faff' : undefined,
                    borderRadius: 16,
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    gap: 8,
                    borderStyle: 'dashed',
                    borderWidth: 2,
                    minHeight: 120,
                    transition: 'all 0.2s ease',
                    transform: !disabled && isDragging ? 'scale(1.02)' : 'scale(1)',
                    ...style,
                }}
            >
                <div
                    className={`fs-4 ${!disabled && isDragging ? 'text-primary' : 'text-secondary'}`}
                    style={{ pointerEvents: 'none' }}
                >
                    {icon ?? <FaCloudUploadAlt />}
                </div>
                <div
                    className={`fw-semibold ${!disabled && isDragging ? 'text-primary' : 'text-dark'}`}
                    style={{ pointerEvents: 'none' }}
                >
                    {title}
                </div>
                {nomes.length ? (
                    <div className="small text-muted" style={{ pointerEvents: 'none' }}>
                        {nomes.join(', ')}
                    </div>
                ) : null}
            </div>
        </>
    );
}
